
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useNotifications } from "@/context/NotificationContext";
import { useFirebaseMessaging, requestNotificationPermission } from "@/services/firebase";

export const useNotificationSystem = () => {
  const [fcmToken, setFcmToken] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();
  const { notifications, addNotification, markAsRead, deleteNotification } = useNotifications();
  const { initializeMessaging } = useFirebaseMessaging();

  useEffect(() => {
    // Initialize Firebase Messaging
    initialize();
  }, []);

  const initialize = async () => {
    setIsLoading(true);
    try {
      console.log("Initializing Firebase Messaging...");
      const result = await initializeMessaging();
      
      if (result.token) {
        setFcmToken(result.token);
        setIsInitialized(true);
        setPermissionDenied(false);
        setErrorMessage(null);
        console.log("Firebase Messaging initialized with token");
        toast({
          title: "Notifications initialized",
          description: "You'll now receive push notifications",
        });
      } else {
        setPermissionDenied(true);
        setErrorMessage(result.error || "Could not initialize notifications");
        console.log("Failed to get FCM token:", result.error);
        
        // Add demo notifications when Firebase fails
        addDemoNotifications();
      }
    } catch (error) {
      console.error("Error initializing Firebase:", error);
      setPermissionDenied(true);
      setErrorMessage(`${error}`);
      
      // Add demo notifications when Firebase fails
      addDemoNotifications();
    } finally {
      setIsLoading(false);
    }
  };

  const requestPermissionAgain = async () => {
    toast({
      title: "Requesting permission...",
    });
    await initialize();
  };

  const addDemoNotifications = () => {
    // Add sample notifications for UI demonstration
    if (notifications.length === 0) {
      addNotification({
        title: "Welcome to Notification Tester",
        body: "This is a sample notification to demonstrate the UI",
      });
      
      setTimeout(() => {
        addNotification({
          title: "How to Test",
          body: "Reveal your FCM token and use it in the Firebase console to send test notifications",
        });
      }, 1000);
    }
  };

  const createTestNotification = () => {
    addNotification({
      title: "Test Notification",
      body: "This is a test notification generated from the app",
    });
    
    toast({
      title: "Test notification created",
    });
  };

  const refreshToken = async () => {
    try {
      setIsLoading(true);
      const result = await requestNotificationPermission();
      if (result.token) {
        setFcmToken(result.token);
        setPermissionDenied(false);
        toast({
          title: "Token refreshed",
          description: "Your FCM token has been updated",
        });
      } else {
        setPermissionDenied(true);
        toast({
          title: "Could not refresh token",
          description: result.error || "Please check browser permissions",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      toast({
        title: "Error refreshing token",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fcmToken,
    isInitialized,
    permissionDenied,
    errorMessage,
    isLoading,
    notifications,
    markAsRead,
    deleteNotification,
    createTestNotification,
    refreshToken,
    requestPermissionAgain
  };
};
